import { Box, Typography, useTheme, Button, InputBase } from "@mui/material";
import { useNavigate }  from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { useState }     from "react";

import PeopleIcon        from "@mui/icons-material/People";
import SearchIcon        from "@mui/icons-material/Search";
import AddIcon           from "@mui/icons-material/Add";
import VisibilityIcon    from "@mui/icons-material/Visibility";

import ReportTable, { RRow, RCell } from "components/Tables/ReportTable";
import FormDialog        from "components/modal";
import CustomerForm      from "../forms/customerForm";
import { useCustomerList }  from "api/hooks/customer";
import { requester }        from "api/pesabackend";
import { setRefreshCount }  from "state/authSlice";

/* ── Status pill ── */
const StatusPill = ({ status }) => {
  const config = status === 1
    ? { label: "Active",   bg: "rgba(29,158,117,0.12)",  color: "#1D9E75" }
    : status === 0
    ? { label: "Inactive", bg: "rgba(186,117,23,0.1)",   color: "#BA7517" }
    : { label: "Unknown",  bg: "rgba(136,135,128,0.12)", color: "#888780" };

  return (
    <Box sx={{
      display: "inline-flex", alignItems: "center", gap: "5px",
      px: "8px", py: "3px", borderRadius: "20px",
      backgroundColor: config.bg,
    }}>
      <Box sx={{ width: "5px", height: "5px", borderRadius: "50%", backgroundColor: config.color, flexShrink: 0 }} />
      <Typography sx={{ fontSize: "11px", fontWeight: 600, color: config.color, lineHeight: 1 }}>
        {config.label}
      </Typography>
    </Box>
  );
};

export default function CustomersTable({ setIsSuccess }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const theme    = useTheme();
  const isDark   = theme.palette.mode === "dark";

  const bodyBg  = theme.palette.background.alt;
  const cardBg  = isDark ? "rgba(255,255,255,0.03)" : "#ffffff";
  const accent  = theme.palette.secondary.main;
  const textPri = theme.palette.neutral.dark;
  const textSec = theme.palette.neutral.main;
  const border  = isDark ? "rgba(29,158,117,0.14)" : "rgba(29,158,117,0.16)";

  const business_branch_id = useSelector((s) => s.authReducer.business_branch_id);
  const permissions        = useSelector((s) => s.authReducer.permissions);
  const { customerList, isLoadingCustomerList } = useCustomerList(1, business_branch_id);

  const [open,          setOpen]          = useState(false);
  const [search,        setSearch]        = useState("");
  const [firstName,     setfirstName]     = useState("");
  const [lastName,      setLastName]      = useState(".");
  const [email,         setEmail]         = useState("a@gmail.com");
  const [address,       setAddress]       = useState("");
  const [phoneNumber,   setPhoneNumber]   = useState("");
  const [isSubmitting,  setIsSubmitting]  = useState(false);

  const saveCustomer = async () => {
    setIsSubmitting(true);
    try {
      await requester.post("/customers/add", {
        related_business: 1,
        related_business_branch: business_branch_id,
        first_name: firstName, last_name: lastName,
        email, address, phone_number: phoneNumber,
      });
      dispatch(setRefreshCount());
      setIsSuccess(true);
      setOpen(false);
    } catch {}
    setIsSubmitting(false);
  };

  const filtered = customerList.filter((r) =>
    `${r.related_user.first_name} ${r.related_user.last_name}`
      .toLowerCase().includes(search.toLowerCase())
  );

  const columns = [
    { label: "#",            width: 40  },
    { label: "First Name"               },
    { label: "Last Name"                },
    { label: "Phone Number"             },
    { label: "Status"                   },
    { label: "Actions",     align: "center" },
  ];

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: bodyBg, p: "1.5rem 2rem", display: "flex", flexDirection: "column", gap: "1.5rem" }}>

      {/* ── Header ── */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "1rem" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <Box sx={{
            width: "40px", height: "40px", borderRadius: "10px",
            backgroundColor: "rgba(29,158,117,0.12)",
            display: "flex", alignItems: "center", justifyContent: "center",
            color: accent, flexShrink: 0,
          }}>
            <PeopleIcon sx={{ fontSize: "22px" }} />
          </Box>
          <Box>
            <Typography sx={{ fontFamily: "'DM Serif Display', serif", fontSize: "22px", fontWeight: 400, color: textPri, lineHeight: 1.2 }}>
              Customers
            </Typography>
            <Typography sx={{ fontSize: "12px", color: textSec, mt: "2px" }}>
              {customerList.length} total customers
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
          {/* Search */}
          <Box sx={{
            display: "flex", alignItems: "center", gap: "6px",
            px: "0.75rem", height: "38px",
            backgroundColor: cardBg,
            border: `1px solid ${border}`,
            borderRadius: "10px", minWidth: "240px",
          }}>
            <SearchIcon sx={{ fontSize: "16px", color: textSec, flexShrink: 0 }} />
            <InputBase
              placeholder="Search customer name…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{ flex: 1, fontSize: "13px", color: textPri }}
            />
          </Box>

          {permissions.CCC === 1 && (
            <Button
              onClick={() => setOpen(true)}
              startIcon={<AddIcon sx={{ fontSize: "16px" }} />}
              sx={{
                fontSize: "13px", fontWeight: 500,
                px: "1rem", py: "0.45rem", borderRadius: "8px",
                backgroundColor: accent, color: "#fff", textTransform: "none",
                "&:hover": { backgroundColor: "#0F6E56" },
              }}
            >
              Create Customer
            </Button>
          )}
        </Box>
      </Box>

      {/* ── Table ── */}
      <ReportTable
        columns={columns}
        rows={filtered}
        loading={isLoadingCustomerList}
        emptyMessage="No customers found"
        renderRow={(row, index) => (
          <RRow key={row.id} index={index}>
            <RCell>{index + 1}</RCell>
            <RCell>
              <Typography sx={{ fontSize: "13px", fontWeight: 500, color: textPri }}>
                {row.related_user.first_name}
              </Typography>
            </RCell>
            <RCell>{row.related_user.last_name}</RCell>
            <RCell>{row.related_user.phone_number || "—"}</RCell>
            <RCell><StatusPill status={row.status} /></RCell>
            <RCell align="center">
              <Box
                onClick={() => navigate("/chartNextAccounts", { state: { id: row.id } })}
                sx={{
                  display: "inline-flex", alignItems: "center", gap: "4px",
                  px: "8px", py: "4px", borderRadius: "6px",
                  border: "1px solid rgba(29,158,117,0.3)",
                  cursor: "pointer", color: accent,
                  fontSize: "12px", fontWeight: 500,
                  transition: "all 0.15s",
                  "&:hover": { backgroundColor: "rgba(29,158,117,0.08)" },
                }}
              >
                <VisibilityIcon sx={{ fontSize: "14px" }} /> View Details
              </Box>
            </RCell>
          </RRow>
        )}
      />

      {/* ── Create customer dialog ── */}
      <FormDialog
        open={open}
        handleClose={() => setOpen(false)}
        enableActions
        title="Add New Customer"
        buttonText="Create Customer"
        handleSubmit={saveCustomer}
        isSubmitting={isSubmitting}
        isSubmittingText="Creating customer, please wait…"
        textStatus
        text={`This will create a new ledger called ${firstName} ${lastName} under Accounts Receivable.`}
        content={
          <CustomerForm
            handleChangeFirstName={(e) => setfirstName(e.target.value)}
            handleChangeLastName={(e)  => setLastName(e.target.value)}
            handleChangeEmailAddress={(e) => setEmail(e.target.value)}
            handleChangeAddress={(e)   => setAddress(e.target.value)}
            handleChangePhoneNumber={(e) => setPhoneNumber(e.target.value)}
          />
        }
      />
    </Box>
  );
}